import React, { useState } from "react";
import styled from "@emotion/styled";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from '@mui/icons-material/Check';
import TagTypeDisplay from "./TagTypeDisplay";
import AssignOrCreateTag from "./AssignOrCreateTag";

const EditTags = ({ leadData, userFullName, teamId, dashPerspective, userId, activeLead, mortgageData }) => {
  const [isError, setIsError] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const leadTagsCount = leadData?.tags?.length || 0;
  const mortgageTagsCount = mortgageData?.tags?.length || 0;

  return (
    <TagsPanel>
      {saveSuccess ?
        <PanelHeader $tone="success">
          <HeaderIcon>
            <CheckIcon />
          </HeaderIcon>
          <HeaderText>Success</HeaderText>
        </PanelHeader>
      : isError ?
        <PanelHeader $tone="error">
          <HeaderIcon>
            <ErrorIcon />
          </HeaderIcon>
          <HeaderText>
            Failed... please try again. Reach out to support@lancastersweep.com if the problem persists.
          </HeaderText>
        </PanelHeader>
      :
        <PanelHeader>
          <HeaderText>
            {leadData && leadData.tags && leadData.tags.length > 0 && mortgageData.tags.length > 0 ?
              `Mortgage and Lead tags`
            : leadData && leadData.tags && leadData.tags.length > 0 ?
              `Lead tags`
            : mortgageData.tags.length > 0 ?
              `Mortgage tags`
            :
              `Tags`
            }
          </HeaderText>
          <HeaderMeta>Review and assign investigation record tags.</HeaderMeta>
        </PanelHeader>
      }
      <TagsBody>
        <TagsScroll>
          {leadData &&
            <TagSection>
              <SectionHeader>
                <SectionTitle>Lead Tags</SectionTitle>
                <SectionCount>{leadTagsCount}</SectionCount>
              </SectionHeader>
              {leadTagsCount > 0 ?
                <TagTypeDisplay tagData={leadData} assignmentType={'query'} />
              :
                <EmptyState>
                  <EmptyTitle>No lead tags</EmptyTitle>
                  <EmptyCopy>Lead-level tags assigned to this investigation will appear here.</EmptyCopy>
                </EmptyState>
              }
            </TagSection>
          }
          <TagSection>
            <SectionHeader>
              <SectionTitle>Mortgage Tags</SectionTitle>
              <SectionCount>{mortgageTagsCount}</SectionCount>
            </SectionHeader>
            {mortgageTagsCount > 0 ?
              <TagTypeDisplay tagData={mortgageData} assignmentType={'query'} tagType={'mortgage'} />
            :
              <EmptyState>
                <EmptyTitle>No mortgage tags</EmptyTitle>
                <EmptyCopy>Mortgage-level tags assigned to this record will appear here.</EmptyCopy>
              </EmptyState>
            }
          </TagSection>
        </TagsScroll>
        <AssignOrCreateTag setIsError={setIsError} setSaveSuccess={setSaveSuccess} leadData={leadData} userFullName={userFullName} teamId={teamId} dashPerspective={dashPerspective} userId={userId} activeLead={activeLead} mortgageData={mortgageData} />
      </TagsBody>
    </TagsPanel>
  )
}

const TagsPanel = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: ${theme.palette.common.cardBackground};
  overflow: hidden;
`)

const PanelHeader = styled.header(({ theme, $tone }) => {
  const isSuccess = $tone === "success";
  const isError = $tone === "error";
  const toneColor = isSuccess
    ? theme.palette.primary.dark
    : isError
      ? theme.palette.common.redDark
      : theme.palette.common.grayText;
  const toneBackground = isSuccess
    ? theme.palette.primary.labelTransparent
    : isError
      ? theme.palette.common.redTransparent
      : theme.palette.common.white;

  return `
    min-height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.7rem;
    padding: 0.8rem 1rem;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${toneBackground};
    color: ${toneColor};
  `;
})

const HeaderIcon = styled.div(({ theme }) => `
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  background: ${theme.palette.common.white};
  border: 0.1rem solid currentColor;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`)

const HeaderText = styled.span`
  color: inherit;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  font-family: -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", 'Inter', "Helvetica Neue", sans-serif;
`

const HeaderMeta = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
`)

const TagsBody = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  background: ${theme.palette.common.grayBorderLight};
  overflow: hidden;
`)

const TagsScroll = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: grid;
  align-content: start;
  gap: 1rem;
  padding: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${theme.palette.common.cardBackground};

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

const TagSection = styled.section(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
`)

const SectionHeader = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
`)

const SectionTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1rem;
  font-weight: 800;
`)

const SectionCount = styled.div(({ theme }) => `
  min-width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999rem;
  background: ${theme.palette.primary.labelTransparent};
  color: ${theme.palette.primary.dark};
  font-size: 0.85rem;
  font-weight: 800;
`)

const EmptyState = styled.div(({ theme }) => `
  display: grid;
  gap: 0.35rem;
  justify-items: center;
  padding: 1.5rem;
  border: 0.1rem dashed ${theme.palette.common.grayBorderDark};
  border-radius: 0.8rem;
  background: ${theme.palette.common.cardBackground};
  text-align: center;
`)

const EmptyTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 1rem;
  font-weight: 800;
`)

const EmptyCopy = styled.div(({ theme }) => `
  max-width: 28rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.4;
`)

export default EditTags;
